import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Check } from 'lucide-react';
import {
  ErrorCode,
  formatTime,
  selfDisclosureFormSteps,
  selfDisclosureStepLabels,
  selfDisclosureSteps,
  type SelfDisclosureFormStep,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Avatar, Banner, Button, Eyebrow } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { portalMagicPath, readPortalMagicToken } from '../../portal/magic';
import { PublicFrame } from '../../reservations/public/PublicFrame';
import {
  LinkExpiredError,
  publicSelfDisclosureKey,
  saveStep,
  submitSelfDisclosure,
  type PublicState,
  type PublicStepSaved,
} from '../api';
import { buildStep, initialForm, stepDiffers, type FieldErrors, type FormState } from './form';
import { ProofsUpload } from './ProofsUpload';
import { SignatureStep, type ConsentsState } from './SignatureStep';
import { AssetsStep, ExpensesStep, IncomeStep, PersonStep } from './steps';
import styles from './SelfDisclosurePublicPage.module.css';

export interface FormFlowProps {
  token: string;
  state: PublicState;
  /** Step to open (the server's `currentStep`, or 1 for a correction). */
  initialStep: number;
  onExpired: (error: LinkExpiredError) => void;
}

type StepNo = 1 | 2 | 3 | 4 | 5;

const TITLES: Record<StepNo, { title: string; lead: string }> = {
  1: { title: 'Angaben zur Person', lead: 'Ihre Kontaktdaten, wie sie in der Selbstauskunft stehen sollen.' },
  2: {
    title: 'Beschäftigung und Einkommen',
    lead: 'Die Angaben brauchen wir, um zu berechnen, welche Finanzierung für Sie tragbar ist. Alle Beträge monatlich.',
  },
  3: {
    title: 'Monatliche Ausgaben',
    lead: 'Laufende Verpflichtungen, die weiterlaufen, wenn Sie die Wohnung kaufen.',
  },
  4: {
    title: 'Vermögen und Nachweise',
    lead: 'Was Sie für den Kauf einsetzen können. Nachweise können Sie jetzt oder später nachreichen.',
  },
  5: {
    title: 'Prüfen und unterschreiben',
    lead: 'Mit Ihrer Unterschrift bestätigen Sie die Angaben. Sie können sie später korrigieren, Ihr Berater sieht dann beide Fassungen.',
  },
};

const NEXT_LABELS: Record<Exclude<StepNo, 5>, string> = {
  1: 'Weiter zu Einkommen',
  2: 'Weiter zu Ausgaben',
  3: 'Weiter zu Vermögen',
  4: 'Weiter zur Unterschrift',
};

function stepKey(step: StepNo): SelfDisclosureFormStep | null {
  return step === 5 ? null : (selfDisclosureFormSteps[step - 1] ?? null);
}

function clampStep(value: number): StepNo {
  return Math.min(5, Math.max(1, Math.round(value))) as StepNo;
}

function without(errors: FieldErrors, field: string): FieldErrors {
  if (!(field in errors)) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

function countText(count: number): string {
  const words: Record<number, string> = {
    1: 'eine Angabe',
    2: 'zwei Angaben',
    3: 'drei Angaben',
    4: 'vier Angaben',
  };
  return words[count] ?? `${count} Angaben`;
}

/** Selbstauskunft 03–05: the five steps with autosave per step, the sidebar stepper, the advisor card. */
export function FormFlow({ token, state, initialStep, onExpired }: FormFlowProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => initialForm(state));
  const [step, setStep] = useState<StepNo>(() => clampStep(initialStep));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [errorCount, setErrorCount] = useState(0);
  const [banner, setBanner] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(state.lastSavedAt);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [consents, setConsents] = useState<ConsentsState>({
    completeness: false,
    sharing: false,
    propertyInfo: false,
  });
  const [signature, setSignature] = useState<string | null>(null);
  const [place, setPlace] = useState(
    () => (state.steps.person ?? state.prefill.person).city ?? state.contact.city ?? '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const formRef = useRef(form);
  formRef.current = form;
  const inFlight = useRef<Promise<boolean> | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const mergeSaved = useCallback(
    (key: SelfDisclosureFormStep, body: unknown, saved: PublicStepSaved) => {
      queryClient.setQueryData<PublicState>(publicSelfDisclosureKey(token), (current) => {
        if (!current) return current;
        const values = Object.fromEntries(
          Object.entries(body as Record<string, unknown>).map(([field, value]) => [field, value ?? null]),
        );
        const previous = (current.steps[key] ?? {}) as Record<string, unknown>;
        return {
          ...current,
          version: saved.version,
          currentStep: Math.max(current.currentStep, saved.currentStep),
          lastSavedAt: saved.lastSavedAt,
          steps: { ...current.steps, [key]: { ...previous, ...values } },
        };
      });
    },
    [queryClient, token],
  );

  /**
   * Saves the step when it is valid and differs from the server's copy. Resolves true when the step is
   * saved (or was already), false when invalid or the request failed. One request at a time.
   */
  const persist = useCallback(
    (key: SelfDisclosureFormStep, showErrors: boolean): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        const built = buildStep(key, formRef.current);
        if (built.body === null) {
          if (showErrors) {
            setErrors(built.errors);
            setErrorCount(Object.keys(built.errors).length);
            setBanner(null);
          }
          return false;
        }
        if (!stepDiffers(key, formRef.current, stateRef.current)) return true;
        setSaving(true);
        setSaveFailed(false);
        try {
          const saved = await saveStep(token, key, built.body);
          mergeSaved(key, built.body, saved);
          setSavedAt(saved.lastSavedAt);
          if (saved.correctionStarted) {
            await queryClient.invalidateQueries({ queryKey: publicSelfDisclosureKey(token) });
          }
          return true;
        } catch (cause) {
          if (cause instanceof LinkExpiredError) {
            onExpired(cause);
            return false;
          }
          setSaveFailed(true);
          if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
            if (showErrors) {
              setErrors(cause.fields);
              setErrorCount(Object.keys(cause.fields).length);
            }
          } else if (showErrors) {
            setBanner(errorMessage(cause));
          }
          return false;
        } finally {
          setSaving(false);
        }
      };
      const chained = (inFlight.current ?? Promise.resolve(true)).then(run, run);
      inFlight.current = chained;
      return chained;
    },
    [mergeSaved, onExpired, queryClient, token],
  );

  // Autosave: 900 ms after the last change of a valid, changed step (silent — no field errors).
  useEffect(() => {
    const key = stepKey(step);
    if (key === null) return;
    const timer = window.setTimeout(() => void persist(key, false), 900);
    return () => window.clearTimeout(timer);
  }, [form, step, persist]);

  function update<K extends keyof FormState>(key: K, patch: Partial<FormState[K]>) {
    setForm((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
    setErrors((current) => {
      if (Object.keys(current).length === 0) return current;
      const next = { ...current };
      for (const field of Object.keys(patch)) delete next[field];
      return next;
    });
  }

  function goTo(next: StepNo) {
    setStep(next);
    setErrors({});
    setErrorCount(0);
    setBanner(null);
    window.setTimeout(() => topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0);
  }

  async function next() {
    const key = stepKey(step);
    if (key === null) return;
    setAdvancing(true);
    try {
      const ok = await persist(key, true);
      if (!ok) return;
      goTo(clampStep(step + 1));
    } finally {
      setAdvancing(false);
    }
  }

  async function jump(target: StepNo) {
    const key = stepKey(step);
    if (key !== null && target > step) {
      const ok = await persist(key, true);
      if (!ok) return;
    } else if (key !== null) {
      void persist(key, false);
    }
    goTo(target);
  }

  async function submit() {
    const next: FieldErrors = {};
    if (!consents.completeness)
      next['consentCompleteness'] = 'Bitte bestätigen Sie, dass Ihre Angaben vollständig und richtig sind.';
    if (!consents.sharing) next['consentSharing'] = 'Bitte willigen Sie in die Weitergabe Ihrer Angaben ein.';
    if (signature === null) next['signaturePng'] = 'Bitte unterschreiben Sie.';
    if (place.trim() === '') next['place'] = 'Bitte geben Sie den Ort an.';
    setErrors(next);
    setErrorCount(Object.keys(next).length);
    setBanner(null);
    if (Object.keys(next).length > 0 || signature === null) return;
    setSubmitting(true);
    try {
      await submitSelfDisclosure(token, {
        consentCompleteness: true,
        consentSharing: true,
        propertyInfoConsent: consents.propertyInfo,
        signaturePng: signature,
        place: place.trim(),
      });
      // Opened from the portal's magic link: back to it for the password (Mandantenportal 08).
      const magic = readPortalMagicToken();
      if (magic) {
        void navigate(portalMagicPath(magic), { state: { fromSubmit: true } });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: publicSelfDisclosureKey(token) });
    } catch (cause) {
      if (cause instanceof LinkExpiredError) {
        onExpired(cause);
      } else if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        setErrors(cause.fields);
        setErrorCount(Object.keys(cause.fields).length);
      } else if (cause instanceof ApiError && cause.code === ErrorCode.TRANSITION_NOT_ALLOWED) {
        const missing = cause.details?.['missing'];
        setBanner(
          Array.isArray(missing) && missing.length > 0 ? missing.map(String).join(' ') : cause.message,
        );
      } else {
        setBanner(errorMessage(cause));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const saved = stateRef.current.steps;
  const doneSteps = new Set<number>();
  selfDisclosureFormSteps.forEach((key, index) => {
    if (saved[key] !== null) doneSteps.add(index + 1);
  });
  const headerEnd = (
    <span className={clsx(styles.savedState, saveFailed && styles.savedStateFailed)}>
      <span className={styles.savedDot} aria-hidden="true" />
      {saving
        ? 'Wird gespeichert …'
        : saveFailed
          ? 'Nicht gespeichert'
          : savedAt
            ? `Gespeichert · ${formatTime(savedAt)}`
            : 'Noch nichts gespeichert'}
    </span>
  );

  const sidebar = (
    <>
      <Eyebrow as="div">Schritt {step} von 5</Eyebrow>
      <ol className={styles.stepper} aria-label="Schritte">
        {selfDisclosureSteps.map((key, index) => {
          const number = (index + 1) as StepNo;
          const stateOf =
            number === step ? 'current' : doneSteps.has(number) || number < step ? 'done' : 'open';
          const clickable = stateOf === 'done' || (number < step && stateOf !== 'current');
          const content = (
            <>
              <span className={clsx(styles.stepCircle, styles[`circle_${stateOf}`])} aria-hidden="true">
                {stateOf === 'done' ? <Check size={11} strokeWidth={2.5} /> : number}
              </span>
              <span className={clsx(styles.stepText, styles[`text_${stateOf}`])}>
                {selfDisclosureStepLabels[key]}
              </span>
            </>
          );
          return (
            <li
              key={key}
              className={styles.stepItem}
              aria-current={stateOf === 'current' ? 'step' : undefined}
            >
              {clickable ? (
                <button
                  type="button"
                  className={clsx(styles.stepRow, styles.stepButton)}
                  onClick={() => void jump(number)}
                >
                  {content}
                </button>
              ) : (
                <span className={clsx(styles.stepRow, stateOf === 'current' && styles.stepRowCurrent)}>
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <div className={styles.advisorCard}>
        <div className={styles.advisorHead}>
          <Avatar initials={state.advisor.initials} name={state.advisor.name} size={40} />
          <div>
            <div className={styles.advisorName}>{state.advisor.name}</div>
            <div className={styles.advisorRole}>Ihr Berater</div>
          </div>
        </div>
        <div className={styles.advisorText}>
          {state.advisor.phone ? (
            <>
              Fragen beim Ausfüllen? Rufen Sie mich an:{' '}
              <a href={`tel:${state.advisor.phone.replace(/\s+/g, '')}`} className={styles.advisorLink}>
                {state.advisor.phone}
              </a>
            </>
          ) : (
            <>
              Fragen beim Ausfüllen? Schreiben Sie mir:{' '}
              <a href={`mailto:${state.advisor.email}`} className={styles.advisorLink}>
                {state.advisor.email}
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );

  const { title, lead } = TITLES[step];
  const key = stepKey(step);

  return (
    <PublicFrame
      context="Selbstauskunft"
      headerEnd={headerEnd}
      sidebar={sidebar}
      legal={{ imprintUrl: null, privacyUrl: state.organization.privacyUrl }}
      organizationName={state.organization.name}
    >
      <div ref={topRef}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.lead}>{lead}</p>
      </div>
      {state.correction ? (
        <Banner tone="info">
          Sie korrigieren Ihre Angaben aus Version {state.correction.previousVersion}. Mit der neuen
          Unterschrift entsteht Version {state.version}; Ihr Berater sieht beide Fassungen.
        </Banner>
      ) : null}
      {errorCount > 0 ? (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorBannerTitle}>Bitte prüfen Sie {countText(errorCount)}.</span>
          <span className={styles.errorBannerText}>Ihre bisherigen Eingaben sind gespeichert.</span>
        </div>
      ) : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}

      {step === 5 ? (
        <SignatureStep
          form={form}
          consents={consents}
          onConsentsChange={(patch) => {
            setConsents((current) => ({ ...current, ...patch }));
            setErrors((current) => {
              const next = { ...current };
              if (patch.completeness) delete next['consentCompleteness'];
              if (patch.sharing) delete next['consentSharing'];
              return next;
            });
          }}
          onSignatureChange={(value) => {
            setSignature(value);
            if (value) setErrors((current) => without(current, 'signaturePng'));
          }}
          place={place}
          onPlaceChange={(value) => {
            setPlace(value);
            setErrors((current) => without(current, 'place'));
          }}
          errors={errors}
          privacyUrl={state.organization.privacyUrl}
          onEdit={() => goTo(1)}
        />
      ) : (
        <div className={styles.card}>
          {step === 1 ? (
            <PersonStep
              form={form.person}
              errors={errors}
              onChange={(patch) => update('person', patch)}
              onBlur={() => key && void persist(key, false)}
            />
          ) : step === 2 ? (
            <IncomeStep
              form={form.income}
              errors={errors}
              onChange={(patch) => update('income', patch)}
              onBlur={() => key && void persist(key, false)}
            />
          ) : step === 3 ? (
            <ExpensesStep
              form={form.expenses}
              errors={errors}
              onChange={(patch) => update('expenses', patch)}
              onBlur={() => key && void persist(key, false)}
            />
          ) : (
            <>
              <AssetsStep
                form={form.assets}
                errors={errors}
                onChange={(patch) => update('assets', patch)}
                onBlur={() => key && void persist(key, false)}
              />
              <ProofsUpload
                token={token}
                proofs={state.proofs}
                onChanged={async () => {
                  await queryClient.invalidateQueries({ queryKey: publicSelfDisclosureKey(token) });
                  setSavedAt(new Date().toISOString());
                }}
                onError={(cause) => {
                  if (cause instanceof LinkExpiredError) onExpired(cause);
                }}
              />
            </>
          )}
        </div>
      )}

      <div className={styles.footerRow}>
        {step > 1 ? (
          <Button variant="secondary" onClick={() => void jump(clampStep(step - 1))} disabled={submitting}>
            Zurück
          </Button>
        ) : null}
        <span className={styles.footerNote}>{step < 5 ? 'Sie können jederzeit unterbrechen' : ''}</span>
        {step === 5 ? (
          <Button size="large" loading={submitting} onClick={() => void submit()}>
            Verbindlich absenden
          </Button>
        ) : (
          <Button size="large" loading={advancing} onClick={() => void next()}>
            {NEXT_LABELS[step]}
          </Button>
        )}
      </div>
    </PublicFrame>
  );
}
